import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Alert,
  LinearProgress,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Transaction } from '../types';
import { smartCategorization } from '../services/smartCategorization';

interface SmartSuggestionsProps {
  transactions: Transaction[];
  currentDescription?: string;
  onCategorySuggestion?: (category: string, confidence: number) => void;
}

const SmartSuggestions: React.FC<SmartSuggestionsProps> = ({ 
  transactions, 
  currentDescription, 
  onCategorySuggestion 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [suggestion, setSuggestion] = useState<any>(null);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    // Initialiser le service avec les transactions
    smartCategorization.init(transactions);
    
    // Obtenir les statistiques
    const serviceStats = smartCategorization.getStats();
    setStats(serviceStats);
    
    // Obtenir les prédictions
    const upcomingExpenses = smartCategorization.predictUpcomingExpenses();
    setPredictions(upcomingExpenses);
  }, [transactions]);

  useEffect(() => {
    if (currentDescription && currentDescription.length > 3) {
      const smartSuggestion = smartCategorization.suggestCategory(currentDescription, 0);
      setSuggestion(smartSuggestion);
      
      if (smartSuggestion && onCategorySuggestion) {
        onCategorySuggestion(smartSuggestion.category, smartSuggestion.confidence);
      }
    } else {
      setSuggestion(null);
    }
  }, [currentDescription, onCategorySuggestion]);

  const getConfidenceColor = (confidence: number) => {
    if (confidence > 0.8) return 'success';
    if (confidence > 0.6) return 'warning';
    return 'info';
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!stats || stats.transactionsAnalyzed === 0) {
    return (
      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PsychologyIcon sx={{ mr: 1, color: 'grey.500' }} />
          <Typography variant="body2" color="text.secondary">
            🤖 L'IA s'entraîne avec vos transactions... Ajoutez quelques transactions pour commencer !
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mb: 2 }}>
      {/* Suggestion en temps réel */}
      {suggestion && (
        <Box sx={{ p: 2, backgroundColor: 'success.light', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: 'success.dark' }} />
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.dark' }}>
              Suggestion IA:
            </Typography>
            <Chip 
              label={suggestion.category}
              color={getConfidenceColor(suggestion.confidence) as any}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
            <Chip 
              label={`${Math.round(suggestion.confidence * 100)}%`}
              variant="outlined"
              size="small"
            />
            {suggestion.isLearned && (
              <Tooltip title="Catégorie apprise de vos habitudes">
                <CheckCircleIcon sx={{ color: 'success.dark', fontSize: 18 }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'success.dark', ml: 4 }}>
            {suggestion.reason}
          </Typography>
        </Box>
      )}

      {/* Header avec statistiques */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={stats.learnedPatterns} color="primary">
            <PsychologyIcon color="primary" />
          </Badge>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              🧠 Assistant IA
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.learnedPatterns} patterns appris • {Math.round(stats.avgConfidence * 100)}% précision
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {predictions.length > 0 && (
            <Chip 
              icon={<TrendingUpIcon />}
              label={`${predictions.length} prédictions`}
              color="info"
              size="small"
            />
          )}
          <IconButton>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          {/* Statistiques détaillées */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>📊 Statistiques d'apprentissage</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="h4" fontWeight="bold">{stats.totalPatterns}</Typography>
                <Typography variant="body2">Patterns reconnus</Typography>
              </Paper>
              <Paper sx={{ p: 2, backgroundColor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h4" fontWeight="bold">{stats.learnedPatterns}</Typography>
                <Typography variant="body2">Patterns appris</Typography>
              </Paper>
              <Paper sx={{ p: 2, backgroundColor: 'info.light', color: 'info.contrastText' }}>
                <Typography variant="h4" fontWeight="bold">{stats.transactionsAnalyzed}</Typography>
                <Typography variant="body2">Transactions analysées</Typography>
              </Paper>
            </Box>
          </Box>
          
          {/* Prédictions des prochaines dépenses */}
          {predictions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon />
                🔮 Prochaines dépenses prédites
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {predictions.slice(0, 5).map((pred, index) => (
                  <Alert 
                    key={index}
                    severity="info"
                    sx={{ 
                      '& .MuiAlert-message': { width: '100%' },
                      backgroundColor: 'info.light',
                      color: 'info.contrastText'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {pred.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip 
                            label={pred.category} 
                            size="small" 
                            color="primary"
                          />
                          <Chip 
                            label={formatDate(pred.nextDate)} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {pred.predictedAmount.toFixed(2)}€
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={pred.confidence * 100}
                            sx={{ width: 50, height: 4 }}
                          />
                          <Typography variant="caption">
                            {Math.round(pred.confidence * 100)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Alert>
                ))}
              </Box>
            </Box>
          )}

          {/* Conseils d'optimisation */}
          <Box>
            <Typography variant="h6" gutterBottom>💡 Conseils d'optimisation</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {stats.learnedPatterns < 5 && (
                <Alert severity="info">
                  Continuez à ajouter des transactions pour améliorer la précision des suggestions !
                </Alert>
              )}
              {predictions.length > 0 && (
                <Alert severity="success">
                  Génial ! L'IA a détecté {predictions.length} dépenses récurrentes. 
                  Vous pouvez anticiper vos budgets.
                </Alert>
              )}
              {stats.avgConfidence > 0.8 && (
                <Alert severity="success">
                  Excellent ! La précision de l'IA est de {Math.round(stats.avgConfidence * 100)}%. 
                  Les suggestions sont très fiables.
                </Alert>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SmartSuggestions;
